import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Crown, Sparkles, Loader2, ArrowLeft, ShieldCheck, Disc3, Music, RadioTower, Brain, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { toast } from 'sonner';
import useSubscription from '@/hooks/useSubscription';

const FEATURES = [
  { icon: Disc3, text: 'Unlimited screenshot scanning + AI recognition' },
  { icon: Tag, text: 'Auto genre crates + dedup across uploads' },
  { icon: Brain, text: 'AI deep dives — bios, similar tracks, music videos' },
  { icon: Music, text: 'BPM & key lookup for every track' },
  { icon: RadioTower, text: 'Spotify auto-match + 1-click playlist export' },
  { icon: ShieldCheck, text: 'Crates persist forever — cancel any time' },
];

/*
 * PricingPage
 *
 * The conversion surface. Monthly / Annual toggle, fully populated feature
 * grid, single primary CTA → backend /billing/checkout → Stripe-hosted page.
 *
 * Notable UX details:
 *   - Annual toggle highlights savings (20% off).
 *   - If Stripe isn't fully configured yet (no price IDs in env), we show
 *     an inline 'Coming soon' state so the page never breaks pre-launch.
 *   - If the user is already Pro, we redirect them to the portal instead.
 */
export default function PricingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [pricing, setPricing] = useState(null);
  const [plan, setPlan] = useState('annual'); // default to highest-value plan
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const { sub, isPro } = useSubscription();

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get('/billing/pricing');
        if (active) setPricing(data);
      } catch (e) {
        toast.error('Could not load pricing');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (searchParams.get('canceled') === '1') {
      toast('Checkout canceled — no worries, you can come back anytime.');
    }
  }, [searchParams]);

  const handleSubscribe = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post('/billing/checkout', {
        plan,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error('Could not create checkout session');
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === 'stripe_not_configured') {
        toast.error('Subscriptions are not active yet. Check back shortly.');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Could not start checkout');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto py-10 px-4 text-center text-muted-foreground">
        <Loader2 className="w-6 h-6 mx-auto animate-spin" />
      </div>
    );
  }

  const monthly = pricing?.monthly || {};
  const annual = pricing?.annual || {};
  const stripeReady = pricing?.stripe_configured;
  const showPrice = plan === 'annual' ? annual.price_usd : monthly.price_usd;
  const showInterval = plan === 'annual' ? 'year' : 'month';
  const effectiveMonthly = plan === 'annual' ? annual.effective_monthly_usd : monthly.price_usd;

  return (
    <div className="max-w-4xl mx-auto py-8 px-4" data-testid="pricing-page">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-4"
        data-testid="pricing-back-button"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back
      </button>

      <header className="text-center mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[hsl(var(--primary))]/10 border border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] text-xs font-mono uppercase mb-3">
          <Crown className="w-3.5 h-3.5" /> GroovLens Pro
        </div>
        <h1 className="font-display text-3xl sm:text-5xl font-semibold tracking-tight">
          One plan. Everything unlocked.
        </h1>
        <p className="text-sm sm:text-base text-muted-foreground mt-3 max-w-2xl mx-auto">
          Unlimited screenshot scans, AI deep dives, BPM & key lookups, Spotify
          export, and crates that stay yours forever.
        </p>
      </header>

      {isPro && (
        <div
          className="mb-6 rounded-xl border border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/10 p-3 text-sm text-center"
          data-testid="pricing-already-pro"
        >
          You&rsquo;re already on Pro. Manage your subscription from your Profile.
        </div>
      )}

      {/* Plan toggle */}
      <div className="flex justify-center mb-6" data-testid="pricing-plan-toggle">
        <div className="inline-flex rounded-xl border border-border bg-card p-1">
          <button
            type="button"
            onClick={() => setPlan('monthly')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              plan === 'monthly'
                ? 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="pricing-plan-monthly"
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setPlan('annual')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${
              plan === 'annual'
                ? 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="pricing-plan-annual"
          >
            Annual
            <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${
              plan === 'annual' ? 'bg-[hsl(var(--primary-foreground))]/20' : 'bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]'
            }`}>
              20% off
            </span>
          </button>
        </div>
      </div>

      {/* Price card */}
      <div className="max-w-md mx-auto rounded-2xl border border-border bg-card p-6 sm:p-8 mb-6" data-testid="pricing-card">
        <div className="text-center mb-5">
          <div className="flex items-baseline justify-center gap-1">
            <span className="text-5xl font-display font-semibold" data-testid="pricing-price">
              ${showPrice}
            </span>
            <span className="text-sm text-muted-foreground">/{showInterval}</span>
          </div>
          {plan === 'annual' && (
            <p className="text-xs text-muted-foreground mt-2">
              That&rsquo;s about <strong className="text-foreground">${effectiveMonthly}/mo</strong> · billed yearly
            </p>
          )}
          {plan === 'monthly' && (
            <p className="text-xs text-muted-foreground mt-2">
              Switch to annual any time to save 20%
            </p>
          )}
        </div>

        <ul className="space-y-2.5 mb-6" data-testid="pricing-features">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <li key={f.text} className="flex items-start gap-2.5 text-sm">
                <Check className="w-4 h-4 text-[hsl(var(--primary))] shrink-0 mt-0.5" />
                <span>{f.text}</span>
              </li>
            );
          })}
        </ul>

        <Button
          onClick={handleSubscribe}
          disabled={submitting || isPro || !stripeReady}
          className="w-full rounded-xl h-11"
          size="lg"
          data-testid="pricing-subscribe-button"
        >
          {submitting ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting to Stripe…</>
          ) : isPro ? (
            <>You&rsquo;re already on Pro</>
          ) : !stripeReady ? (
            <>Subscriptions activating soon</>
          ) : (
            <><Sparkles className="w-4 h-4 mr-1.5" /> Subscribe — ${showPrice}/{showInterval}</>
          )}
        </Button>
        {!stripeReady && (
          <p className="text-[11px] text-center text-muted-foreground mt-3" data-testid="pricing-stripe-pending">
            We&rsquo;re finalising payment setup. Trial users keep full access in the meantime.
          </p>
        )}
        <p className="text-[11px] text-center text-muted-foreground mt-3">
          Secure checkout via Stripe. Cancel any time from your Profile.
        </p>
      </div>

      <p className="text-center text-xs text-muted-foreground">
        Questions? Email <a href="mailto:hello@groovlens.com" className="underline hover:text-foreground">hello@groovlens.com</a>
      </p>
    </div>
  );
}
